import * as Api from '@ViewModels';
import { PersistentStorageManager } from './Storage';

export const ManagedAccountAccessTokenType = 'managed_account_access';

const MANAGED_ACCOUNT_ACCESS_SESSION_STORAGE_KEY = 'managedAccountAccessSession';
const SECONDS_TO_MILLISECONDS_THRESHOLD = 1_000_000_000_000;

export interface IManagedAccountAccessAuthToken extends Omit<Api.ICredential, 'expires_utc'> {
	expires_in?: number;
	expires_utc?: number | string;
}

export interface IManagedAccountAccessSessionPrincipal {
	id?: string;
	name?: string;
	email?: string;
}

export interface IManagedAccountAccessSessionAccount {
	id?: string;
	companyName?: string;
}

/**
 * Client-side metadata for a managed account access session.
 * The parent credential is stored only to support an explicit/expiry-driven return to the management account.
 */
export interface IManagedAccountAccessSession {
	parentCredential: Api.ICredential;
	parentAccount: IManagedAccountAccessSessionAccount;
	parentUser: IManagedAccountAccessSessionPrincipal;
	managedAccount: IManagedAccountAccessSessionAccount;
	managedUser: IManagedAccountAccessSessionPrincipal;
	expiresUtc: number;
	startedUtc: number;
}

export function normalizeCredentialExpiresUtc(expiresUtc?: number | string, expiresInSeconds?: number): number {
	if (typeof expiresUtc === 'number' && Number.isFinite(expiresUtc)) {
		return expiresUtc < SECONDS_TO_MILLISECONDS_THRESHOLD ? expiresUtc * 1000 : expiresUtc;
	}

	if (typeof expiresUtc === 'string' && expiresUtc.trim()) {
		const parsedDate = Date.parse(expiresUtc);
		if (!Number.isNaN(parsedDate)) {
			return parsedDate;
		}

		const parsedNumber = Number(expiresUtc);
		if (Number.isFinite(parsedNumber)) {
			return parsedNumber < SECONDS_TO_MILLISECONDS_THRESHOLD ? parsedNumber * 1000 : parsedNumber;
		}
	}

	return Date.now() + (expiresInSeconds ?? 0) * 1000;
}

export function normalizeManagedAccountAccessCredential(token: IManagedAccountAccessAuthToken): Api.ICredential {
	return {
		access_token: token.access_token,
		account_id: token.account_id,
		expires_utc: normalizeCredentialExpiresUtc(token.expires_utc, token.expires_in),
		refresh_token: token.refresh_token,
		token_type: token.token_type ?? ManagedAccountAccessTokenType,
		user_id: token.user_id,
	};
}

export function isManagedAccountAccessCredential(credential?: Api.ICredential | null): boolean {
	return credential?.token_type === ManagedAccountAccessTokenType;
}

export async function getManagedAccountAccessSession(): Promise<IManagedAccountAccessSession | null> {
	try {
		return await PersistentStorageManager.local.getObject<IManagedAccountAccessSession>(
			MANAGED_ACCOUNT_ACCESS_SESSION_STORAGE_KEY
		);
	} catch {
		return null;
	}
}

export async function setManagedAccountAccessSession(session: IManagedAccountAccessSession) {
	return PersistentStorageManager.local.setObject(MANAGED_ACCOUNT_ACCESS_SESSION_STORAGE_KEY, session);
}

export function clearManagedAccountAccessSession() {
	PersistentStorageManager.local.remove(MANAGED_ACCOUNT_ACCESS_SESSION_STORAGE_KEY);
}
